import React from "react";
import { 
  Card, 
  CardContent, 
  CardMedia, 
  Typography, 
  Button, 
  Grid, 
  Box, 
  Container, 
  Chip 
} from "@mui/material";
import { 
  DirectionsBike, 
  Speed, 
  LocalGasStation, 
  Bolt, 
  Star 
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// Updated Data with Correct, High-Quality Images
const bikes = [
  {
    category: "Adventure",
    name: "Royal Enfield Himalayan 411",
    image:
      "https://c.ndtvimg.com/2023-11/krcee13g_royal-enfield-himalayan-450-650_625x300_24_November_23.jpg?downsize=545:307",
    engine: "411 cc",
    fuel: "Petrol",
    speed: "130–135 km/h",
    power: "24.3 HP",
  },
  {
    category: "Adventure / Dual Sport",
    name: "Hero Xpulse 200 4V",
    image:
      "https://www.bajajauto.com/corporate/media-centre/press-releases/-/media/Images/bajajauto/media-kit/media-centre/dominar-ladakh-tour.ashx",
    engine: "199.6 cc",
    fuel: "Petrol",
    speed: "115–120 km/h",
    power: "18.9 HP",
  },
  {
    category: "Sports Tourer",
    name: "Bajaj Dominar 400",
    image:
      "https://apollo.olx.in/v1/files/l3e2jjmugmlf1-IN/image;s=272x0",
    engine: "373.3 cc",
    fuel: "Petrol",
    speed: "150–155 km/h",
    power: "40 HP",
  },
];

const BikeRentalComponent = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate("/bikebooking");
  };

  return (
    <Box sx={{ backgroundColor: "#F8F9FA", py: 8, px: 2 }}>
      <Container maxWidth="xl">
        
        {/* --- HERO SECTION --- */}
        <Box 
          sx={{ 
            textAlign: "center", 
            mb: 8,
            position: 'relative',
            '&::after': {
              content: '""',
              display: 'block',
              width: '80px',
              height: '4px',
              backgroundColor: '#FFD046',
              margin: '10px auto',
              borderRadius: '2px'
            }
          }}
        >
          <Typography 
            variant="overline" 
            sx={{ 
              color: "#FFD046", 
              fontWeight: 800, 
              letterSpacing: 3, 
              textTransform: 'uppercase' 
            }}
          >
            Motorcycle Rentals
          </Typography>
          <Typography 
            variant="h3" 
            fontWeight="900" 
            sx={{ color: "#091C47", fontSize: { xs: "2rem", md: "3rem" } }}
          >
            Ride The <span style={{ color: "#FFD046" }}>Wilderness</span>
          </Typography>
          <Typography 
            variant="subtitle1" 
            color="text.secondary" 
            sx={{ maxWidth: 600, mx: "auto", mt: 2 }}
          >
            Experience freedom on two wheels. Premium adventure bikes ready for your next journey.
          </Typography>
        </Box>

        <Grid container spacing={4} justifyContent="center">
          {bikes.map((bike, index) => (
            <Grid item key={index} xs={12} sm={6} md={4}>
              <Card 
                sx={{ 
                  borderRadius: "20px", 
                  overflow: "hidden", 
                  boxShadow: "0 10px 40px rgba(0,0,0,0.08)", 
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  border: '1px solid rgba(0,0,0,0.03)',
                  "&:hover": { 
                    transform: "translateY(-8px)", 
                    boxShadow: "0 20px 50px rgba(9, 28, 71, 0.15)" 
                  }
                }}
              >
                {/* Image with Category Badge */}
                <Box sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="220"
                    image={bike.image}
                    alt={bike.name}
                    sx={{ objectFit: "contain", borderBottom: "4px solid #FFD046" }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 16,
                      left: 16,
                      backgroundColor: "#091C47",
                      color: "#fff",
                      px: 2,
                      py: 0.5,
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      textTransform: "uppercase",
                      boxShadow: 2
                    }}
                  >
                    {bike.category}
                  </Box>
                </Box>

                <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  
                  {/* Title & Rating */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Typography variant="h6" fontWeight="800" color="#091C47">
                      {bike.name}
                    </Typography>
                    <Box display="flex" alignItems="center" color="#FFD046">
                      <Star sx={{ fontSize: 16 }} />
                      <Typography variant="caption" fontWeight="bold" sx={{ ml: 0.5, color: "#666" }}>4.9</Typography>
                    </Box>
                  </Box>
              
                  <Box 
                    sx={{
                      backgroundColor: "#FFF9E6",
                      color: "#B27D00",
                      py: 1,
                      px: 2,
                      borderRadius: "8px",
                      textAlign: "center",
                      mb: 2,
                      fontWeight: 600,
                      display: 'inline-block',
                      alignSelf: 'flex-start'
                    }}
                  >
                    Starting at {bike.price}
                  </Box>

                  {/* Specs Grid */}
                  <Grid container spacing={1.5} sx={{ mb: 3, backgroundColor: "#F8F9FA", p: 2, borderRadius: 2 }}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                        <DirectionsBike sx={{ color: "#091C47", fontSize: 18 }} /> {bike.engine}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                        <LocalGasStation sx={{ color: "#091C47", fontSize: 18 }} /> {bike.fuel}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                        <Speed sx={{ color: "#091C47", fontSize: 18 }} /> {bike.speed}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={1}>
                        <Bolt sx={{ color: "#091C47", fontSize: 18 }} /> {bike.power}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Action Buttons */}
                  <Box sx={{ mt: "auto", display: "flex", gap: 1, height: "45px" }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        borderRadius: "50px 0 0 50px",
                        borderColor: "#E0E0E0",
                        color: "#555",
                        fontWeight: 600,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#f5f5f5", borderColor: "#bbb" }
                      }}
                    >
                      View Details
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleBooking}
                      sx={{
                        borderRadius: "0 50px 50px 0",
                        backgroundColor: "#091C47",
                        color: "#fff",
                        fontWeight: 700,
                        textTransform: "none",
                        boxShadow: "0 4px 10px rgba(9, 28, 71, 0.3)",
                        "&:hover": { backgroundColor: "#0f2d6b" }
                      }}
                    >
                      Book Now
                    </Button>
                  </Box>

                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default BikeRentalComponent;